import { useEffect, useRef, useState, useCallback } from 'react';
import { sameAccount } from '../../../utils/accountIds';
import { accountOfService, directoryAccount, useCaseAccount } from './useCaseAccount';
import { FormState } from 'react-hook-form';
import Popup from 'reactjs-popup';
import { Account, Attachment, InventoryAsset, SupportCasesItem, User } from '../../../types';
import { Icon } from '../../Atoms/Icon';
import { AttachmentQueue } from '../AttachmentQueue';
import { ServiceSelectionForm } from '../CaseCreation/ServiceSelectionForm';
import { CaseCreatedForm } from '../CaseCreation/CaseCreatedForm';
import { FormData, CaseForm } from '../CaseCreation/CaseForm';
import { Wizard, WizardConfiguration } from './Wizard';
import { useAttachments } from '../../../store/Attachment';
import { CaseRequest } from '../../../Api/Cases/postCase';
import { addCase } from '../../../store/SupportCase';
import { getAccountScope, getUserSubject } from '../../../store/User';
import { getInventoryLoadingSubject } from '../../../store/Inventory';
import { combineLatest } from 'rxjs';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { getQueuedAttachmentsSubject } from '../../../store/Attachment';
import { TextSpan } from '../../Atoms/Text';
import { PostSharedAccountCustomizationRequest } from '../../../Api/Preferences/SharedAccountCustomizations/sharedAccountCustomizationsApi';
import {
  SharedAccountCustomizationData,
  createSharedAccountCustomizations,
  defaultSharedAccountCustomizationData,
  fetchSharedAccountCustomizations,
  getSharedAccountCustomizationsLoadingSubject,
  getSharedAccountCustomizationsSubject,
  updateSharedAccountCustomizations
} from '../../../store/SharedAccountCustomizations';
import { fetchAggregateInventory } from '../../../Api/Inventory/aggregateInventoryApi';
import { eligibleServices } from '../CaseCreation/eligibleServices';
import { MODAL_CONTENT_STYLE } from '../../../utils/styleHelpers';

const contentStyle = { ...MODAL_CONTENT_STYLE, width: '414px', minHeight: '400px' };

export interface LocationServices {
  address: string;
  accountId: string;
  services: InventoryAsset[];
}

export interface SupportCaseWizardProps {
  ctaClassName: string;
  onFormSubmissionHandler?: () => Promise<void>;
  onFormCloseHandler?: () => Promise<void>;
  onCaseCreated?: (case_: SupportCasesItem, account: Account) => void;
  inventoryAsset?: InventoryAsset;
  location?: LocationServices;
  disabled?: boolean;
  disabledMessage?: string;
}

const ACCOUNT_NOT_FOUND = 'The account this case would be filed under could not be confirmed.';

export function SupportCaseWizard({
  ctaClassName,
  onFormSubmissionHandler,
  onFormCloseHandler,
  onCaseCreated,
  inventoryAsset,
  location,
  disabled = false,
  disabledMessage = ''
}: SupportCaseWizardProps) {
  const [open, setOpen] = useState(false);
  const [configurations, setConfigurations] = useState<WizardConfiguration[]>([]);
  const [service, setService] = useState<InventoryAsset | null>(inventoryAsset || null);
  const [casePayload, setCasePayload] = useState<CaseRequest | null>(null);
  const [case_, setCase_] = useState<SupportCasesItem | null>(null);
  const [caseFormFilledOut, setCaseFormFilledOut] = useState<boolean>(false);
  const [caseFormSubmitted, setCaseFormSubmitted] = useState<boolean>(false);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [inventory, setInventory] = useState<InventoryAsset[] | undefined>(undefined);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [queuedAttachments, setQueuedAttachments] = useState<Attachment[]>([]);
  const [additionalRecipients, setAdditionalRecipients] = useState<string[]>([]);
  const [saveDefault, setSaveDefault] = useState<boolean>(false);
  const [acctCust, setAcctCust] = useState<SharedAccountCustomizationData>();
  const selectedAccountRef = useRef<Account | undefined>(undefined);

  const { setSnack } = useSnackbar();
  const { uploadAttachments, cancelUpload, retryUpload, setParentId, clear } = useAttachments();

  const determineSelectedAccount = useCallback(
    (
      currentInventoryAsset: InventoryAsset | undefined,
      accountsInScope: Account[] | undefined
    ): Account | undefined => {
      if (currentInventoryAsset && accountsInScope) {
        return accountOfService(currentInventoryAsset) || accountsInScope[0];
      }
      return accountsInScope?.length === 1 ? accountsInScope[0] : undefined;
    },
    []
  );

  const initialAccount = () => (location ? undefined : determineSelectedAccount(inventoryAsset, getAccountScope()));

  const {
    selectedAccount,
    setSelectedAccount,
    accountPending,
    adoptLocation,
    adoptService,
    dropLookup,
    reset: resetAccount
  } = useCaseAccount(initialAccount, () => setSnack({ message: ACCOUNT_NOT_FOUND, type: 'error', open: true }));

  useEffect(() => {
    selectedAccountRef.current = selectedAccount;
  }, [selectedAccount]);

  useEffect(() => {
    if (inventoryAsset) {
      setService(inventoryAsset);
      setConfigurations(twoStepConfiguration);
    } else {
      setConfigurations(threeStepConfiguration);
    }

    const userSub = getUserSubject().subscribe((userData) => setUser(userData));
    const attachmentSubscription = getQueuedAttachmentsSubject().subscribe((queuedAttachments) => {
      setQueuedAttachments(queuedAttachments);
    });

    const loadingSubscription = combineLatest([
      getInventoryLoadingSubject(),
      getSharedAccountCustomizationsLoadingSubject()
    ]).subscribe((data) => setDataLoading(data.some((i) => i)));

    const sharedAccountCustomizationsSub = getSharedAccountCustomizationsSubject().subscribe((ac) => {
      if (ac) {
        const currentAcctCust: SharedAccountCustomizationData = JSON.parse(
          JSON.stringify(
            ac.find((i) => sameAccount(i?.account_id, selectedAccountRef.current?.id)) ||
              defaultSharedAccountCustomizationData(selectedAccountRef.current?.id, selectedAccountRef.current?.name)
          )
        );
        setAcctCust(currentAcctCust);

        const defaultRecip = currentAcctCust?.content?.additionalRecipients || [];
        setAdditionalRecipients(defaultRecip);
      }
    });

    return () => {
      if (attachmentSubscription) attachmentSubscription.unsubscribe();
      if (userSub) userSub.unsubscribe();
      if (sharedAccountCustomizationsSub) sharedAccountCustomizationsSub.unsubscribe();
      if (loadingSubscription) loadingSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (case_?.sfId) setParentId(case_.sfId);
  }, [case_?.sfId]);

  useEffect(() => {
    if (!location) getAssets();
    if (selectedAccount?.id) {
      setAcctCust(undefined);
      setAdditionalRecipients([]);
      fetchSharedAccountCustomizations([selectedAccount.id]);
    }
  }, [selectedAccount?.id]);

  const getAssets = async () => {
    if (selectedAccount) {
      setDataLoading(true);
      const { data } = await fetchAggregateInventory([selectedAccount.id]);
      // Corporate proxies can replace a 200 body with non-JSON — treat anything non-array as an error.
      if (Array.isArray(data)) {
        const inventoryAssets = eligibleServices(data);
        setInventory(inventoryAssets);
        if (!inventoryAssets.length) {
          setSnack({ message: 'No services found for selected account.', type: 'warning', open: true });
        }
      } else {
        setSnack({ message: 'There was an error retrieving assets.', type: 'error', open: true });
      }
      setDataLoading(false);
    }
  };

  const handleSelectInventory = (selectedService: InventoryAsset | null): void => {
    setService(selectedService);
    if (!selectedService) dropLookup();
    else if (location) adoptService(selectedService, location.accountId);
  };

  const handleValidateCaseForm = (formState: FormState<FormData>) => {
    const { isValid } = formState;

    setCaseFormFilledOut(isValid);
  };

  const handleOpenForm = () => {
    if (location) adoptLocation(location.accountId);
    else setSelectedAccount(initialAccount());
    setOpen(true);
  };

  const resetWizard = () => {
    setService(inventoryAsset || null);
    setCasePayload(null);
    setInventory(undefined);
    setCase_(null);
    setCaseFormFilledOut(false);
    setCaseFormSubmitted(false);
    setAcctCust(undefined);
    setSaveDefault(false);
    setAdditionalRecipients([]);
    resetAccount();
    clear();
  };

  const closeWizard = async () => {
    setOpen(false);
    resetWizard();

    if (caseFormSubmitted && onFormSubmissionHandler) {
      await onFormSubmissionHandler();
    }
    if (onFormCloseHandler) {
      await onFormCloseHandler();
    }
  };

  const saveAccountCust = async (currentAcctCust: SharedAccountCustomizationData) => {
    if (selectedAccount?.id) {
      const req: PostSharedAccountCustomizationRequest = {
        account_id: selectedAccount?.id || '',
        account_name: selectedAccount?.name || '',
        content: currentAcctCust.content
      };

      const res =
        currentAcctCust?.id && sameAccount(currentAcctCust.account_id, selectedAccount.id)
          ? await updateSharedAccountCustomizations(currentAcctCust.id, req)
          : await createSharedAccountCustomizations(req);

      if (res) {
        setSnack({ message: 'Settings Saved', open: true, type: 'success' });
      }
    }
  };

  const handleSubmitCaseForm = async (formData: FormData): Promise<void> => {
    const issueType =
      formData?.isServiceDown === 'true' || formData?.serviceSubject === 'Other' ? 'General Support' : 'Customer Care';
    // A saved account selection can carry an outdated Salesforce id, so the directory answers first.
    const currentAccount = await directoryAccount(selectedAccount?.id || '');
    const requestBody: CaseRequest = {
      Status: 'New',
      AccountId: currentAccount?.sfId || selectedAccount?.sfId || '',
      ContactId: user?.id,
      Description: formData?.Description,
      Type: issueType,
      RecordTypeId: '0125G000000bKmIQAU',
      Reason: formData?.Reason,
      Priority: formData?.Priority,
      OrderProductId: service?.sfId || '',
      OutsideInventoryId: service?.outside_inventory_schema_id ? service.id : '',
      AdditionalRecipients: additionalRecipients.join(', '),
      supplierCaseNumber: formData?.supplierCaseNumber
    };
    setCasePayload(requestBody);
    setCaseFormSubmitted(true);

    if (saveDefault && additionalRecipients.length > 0) {
      const currentAcctCust = { ...acctCust } as SharedAccountCustomizationData;
      if (currentAcctCust?.content) {
        currentAcctCust.content.additionalRecipients = additionalRecipients;
      }
      saveAccountCust(currentAcctCust);
    }
  };

  async function handlePostCase() {
    if (casePayload) {
      const newCase = await addCase(casePayload);
      if (newCase) {
        setSnack({ message: 'Your support case has been created successfully', type: 'success', open: true });
        setCase_(newCase);
        if (selectedAccount) onCaseCreated?.(newCase, selectedAccount);
      } else {
        setSnack({ message: 'There was an error sending your request.', type: 'error', open: true });
        setCaseFormSubmitted(false);
      }
    }
  }

  const handleSetConfigurations = () => {
    setConfigurations(inventoryAsset ? twoStepConfiguration : threeStepConfiguration);
  };

  const threeStepConfiguration: WizardConfiguration[] = [
    {
      step: 1,
      childComponent: 0,
      headerCopy: 'Submit a Case',
      progressBarCopy: 'Select a Service',
      prevStep: null,
      nextStep: 2,
      showExitBtn: true,
      exitBtnCopy: 'Cancel',
      canMoveNextStep: service !== null && selectedAccount !== undefined && !accountPending,
      canMovePrevStep: false,
      handleExitWizard: () => closeWizard()
    },
    {
      step: 2,
      childComponent: 1,
      headerCopy: 'Submit a Case',
      progressBarCopy: 'Case Details',
      nextBtnCopy: 'Submit Case',
      nextBtnForm: 'case-creation-form',
      submitForm: caseFormSubmitted ? handlePostCase : null,
      formSubmissionComplete: case_ !== null,
      prevStep: 1,
      nextStep: 3,
      showExitBtn: true,
      exitBtnCopy: 'Cancel',
      canMoveNextStep: caseFormFilledOut,
      canMovePrevStep: true,
      handleExitWizard: () => closeWizard()
    },
    {
      step: 3,
      childComponent: 2,
      headerCopy: 'Case Created',
      progressBarCopy: 'Attachments',
      nextBtnCopy: 'Done',
      prevStep: 2,
      nextStep: null,
      blockPrevStep: true,
      canMoveNextStep: true,
      canMovePrevStep: false,
      handleExitWizard: () => closeWizard()
    }
  ];

  const twoStepConfiguration: WizardConfiguration[] = [
    {
      step: 1,
      childComponent: 1,
      headerCopy: 'Submit a Case',
      progressBarCopy: 'Case Details',
      nextBtnCopy: 'Submit Case',
      nextBtnForm: 'case-creation-form',
      submitForm: caseFormSubmitted ? handlePostCase : null,
      formSubmissionComplete: case_ !== null,
      prevStep: null,
      nextStep: 2,
      showExitBtn: true,
      exitBtnCopy: 'Cancel',
      canMoveNextStep: caseFormFilledOut,
      canMovePrevStep: true,
      handleExitWizard: () => closeWizard()
    },
    {
      step: 2,
      childComponent: 2,
      headerCopy: 'Case Created',
      progressBarCopy: 'Attachments',
      nextBtnCopy: 'Done',
      prevStep: 1,
      nextStep: null,
      blockPrevStep: true,
      canMoveNextStep: true,
      canMovePrevStep: false,
      handleExitWizard: () => closeWizard()
    }
  ];

  useEffect(() => {
    setService(inventoryAsset || null);
  }, [inventoryAsset]);

  useEffect(() => {
    handleSetConfigurations();
  }, [service, case_, caseFormFilledOut, caseFormSubmitted, accountPending, selectedAccount?.id]);

  let content;

  if (disabled) {
    content = (
      <Popup
        className="custom-tooltip"
        trigger={
          <div className="flex items-center space-x-2">
            <Icon type="plusDisabled" />
            <button
              className={`${ctaClassName} text-slate-400`}
              data-cy="new-case"
              onClick={() => handleOpenForm()}
              disabled={true}>
              New Case
            </button>
          </div>
        }
        on="hover"
        position="bottom right">
        <p>{disabledMessage}</p>
      </Popup>
    );
  } else {
    content = (
      <TextSpan className="flex items-center space-x-2">
        <Icon type="plus" />
        <button
          className={`${ctaClassName} ${disabled ? 'text-slate-400' : ''}`}
          data-cy="new-case"
          onClick={() => handleOpenForm()}
          disabled={disabled}>
          New Case
        </button>
        <Popup
          closeOnDocumentClick={false}
          open={open}
          modal={true}
          contentStyle={contentStyle}>
          <Wizard
            configurations={configurations}
            dataLoading={dataLoading || accountPending}>
            <ServiceSelectionForm
              onSubmit={handleSelectInventory}
              services={location?.services || inventory || []}
              selectedService={service}
              selectedAccount={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              locationAddress={location?.address}
              accountNote={
                location !== undefined && !accountPending && selectedAccount === undefined
                  ? ACCOUNT_NOT_FOUND
                  : undefined
              }
            />
            <div>
              {service && selectedAccount && (
                <CaseForm
                  service={service}
                  onSubmit={handleSubmitCaseForm}
                  onChange={handleValidateCaseForm}
                  additionalRecipients={additionalRecipients}
                  setAdditionalRecipients={setAdditionalRecipients}
                  saveDefault={saveDefault}
                  setSaveDefault={(_e, v) => setSaveDefault(v)}
                  onCloseForm={closeWizard}
                />
              )}
            </div>
            <div>
              {case_ && (
                <CaseCreatedForm
                  addAttachments={uploadAttachments}
                  data={case_}
                  headerCopy={`Case #${case_.caseNumber}`}
                  bodyCopy="Your case has been sent to UPSTACK successfully. You can now optionally upload relevant attachments to your case"
                  ctaCopy="Add Attachment"
                />
              )}
              {queuedAttachments.length > 0 && (
                <>
                  <hr className="mt-5 mb-5" />
                  <AttachmentQueue
                    queuedAttachments={queuedAttachments}
                    cancelUpload={cancelUpload}
                    retryUpload={retryUpload}
                  />
                </>
              )}
            </div>
          </Wizard>
        </Popup>
      </TextSpan>
    );
  }
  return content;
}
